import {
  ActivityChangeOptions,
  ActivityItem,
  ActivityTagItem,
} from '@spotify/plugin-confidence-activity-react';
import { createMetricUpdatesChangeExtension } from '@spotify/plugin-confidence-metrics-react';

import {
  formatDuration,
  parseDuration,
} from '../../../components/ScheduleIntervalsSection/utils/durationUtils';

interface VarianceReductionConfig {
  disabled?: boolean;
  aggregationWindowOverride?: string;
}

const formatOverride = (override: string): string =>
  formatDuration(parseDuration(override));

const VarianceReductionConfigChangeRender = ({
  change,
}: ActivityChangeOptions) => {
  const oldValue = change.oldValue as VarianceReductionConfig | null;
  const newValue = change.newValue as VarianceReductionConfig | null;

  const wasDisabled = oldValue?.disabled ?? true;
  const isDisabled = newValue?.disabled ?? true;
  const enabledChanged = wasDisabled !== isDisabled;

  const oldOverride = oldValue?.aggregationWindowOverride;
  const newOverride = newValue?.aggregationWindowOverride;
  const overrideAdded = !oldOverride && !!newOverride;
  const overrideRemoved = !!oldOverride && !newOverride;
  const overrideUpdated =
    !!oldOverride && !!newOverride && oldOverride !== newOverride;

  return (
    <>
      {enabledChanged && (
        <ActivityItem.Section label="Updated variance reduction">
          <ActivityTagItem
            oldValue={wasDisabled ? 'Disabled' : 'Enabled'}
            newValue={isDisabled ? 'Disabled' : 'Enabled'}
          />
        </ActivityItem.Section>
      )}

      {!isDisabled && overrideAdded && (
        <ActivityItem.Section label="Added aggregation window override">
          <ActivityTagItem newValue={formatOverride(newOverride)} />
        </ActivityItem.Section>
      )}

      {overrideRemoved && (
        <ActivityItem.Section label="Removed aggregation window override">
          <ActivityTagItem oldValue={formatOverride(oldOverride)} />
        </ActivityItem.Section>
      )}

      {!isDisabled && overrideUpdated && (
        <ActivityItem.Section label="Updated aggregation window override">
          <ActivityTagItem
            oldValue={formatOverride(oldOverride)}
            newValue={formatOverride(newOverride)}
          />
        </ActivityItem.Section>
      )}
    </>
  );
};

export const METRIC_VARIANCE_REDUCTION_CONFIG_CHANGE_EXTENSION =
  createMetricUpdatesChangeExtension({
    id: 'metric-variance-reduction-config-change',
    predicate: ({ change }) =>
      change.fieldPath === '/variance_reduction_config',
    render: VarianceReductionConfigChangeRender,
  });
